import { useEffect, useState } from 'react';
import { Check, Cloud, CloudOff, CloudUpload, RefreshCw, X } from 'lucide-react';
import { ModalOverlay } from './ModalOverlay';
import { extractApiMessage } from '@/lib/api';
import {
  discardPendingOperation,
  resolveOperationConflict,
  syncPendingOperations,
  usePendingOperations,
} from '@/lib/offline/operations';
import type { PendingOperation } from '@/lib/offline/db';
import type { OfflineBundle, OfflineRow } from '@/lib/offline/domainTypes';
import { getSensorEquipment, type SensorEquipment } from '@/lib/sensorEquipment';
import type { NaveSensores } from '@/lib/queries/iot';

const button =
  'min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-medium disabled:opacity-50';
const names: Record<string, string> = {
  camadas: 'Camada',
  naves: 'Nave',
  'lotes-nave': 'Asignación de nave',
  'entradas-animales': 'Entrada de animales',
  'registros-diarios': 'Parte diario',
  pesajes: 'Muestreo de peso',
  'entregas-pienso': 'Entrega de pienso',
  salidas: 'Salida a matadero',
  liquidacion: 'Liquidación',
  'curva-esperada': 'Curva de peso',
  'curva-pienso': 'Curva de pienso',
  scenario: 'Escenario de sensores',
  sensores: 'Sensores de nave',
};
const fields: Record<string, string> = {
  nombre: 'Nombre',
  codigo: 'Código',
  codigo_lote: 'Camada',
  fecha: 'Fecha',
  fecha_entrada: 'Entrada',
  fecha_final: 'Fecha final',
  fecha_carga: 'Carga',
  fecha_prevista_salida: 'Salida prevista',
  edad_dias_entrada: 'Edad de entrada',
  edad_entrada: 'Edad de entrada',
  num_animales: 'Animales',
  num_animales_asignados: 'Aves asignadas',
  num_animales_total: 'Animales',
  kg: 'Pienso (kg)',
  kg_total: 'Peso total (kg)',
  kg_entrada: 'Peso de entrada (kg)',
  kg_salida: 'Peso de salida (kg)',
  kg_pienso: 'Pienso (kg)',
  bajas_muerte: 'Muertes',
  bajas_triaje: 'Triajes',
  bajas_registradas: 'Bajas',
  observaciones: 'Observaciones',
  num_documento_traslado: 'Documento de traslado',
  num_albaran_procavi: 'Albarán',
  numero_albaran: 'Albarán',
  numero_camion: 'Camión',
  matadero: 'Matadero',
  unidades_cargadas: 'Aves cargadas',
  unidades_cargadas_total: 'Aves cargadas',
  kg_confirmados_destino: 'Peso de destino (kg)',
  capacidad_animales: 'Capacidad',
  peso_medio_kg: 'Peso medio (kg)',
  pesos_g: 'Pesos de muestra (g)',
  sexo: 'Sexo',
  fase: 'Fase',
  cerrada: 'Cerrada',
  puntos: 'Puntos',
  partes: 'Naves',
  lineas: 'Naves',
  edad_dias: 'Edad (días)',
  kg_acumulados: 'Pienso acumulado (kg)',
  modo: 'Modo',
  cambios: 'Ajustes',
  datos: 'Datos',
  resultado: 'Resultado',
  configuracion: 'Configuración',
  fuentes: 'Registros de referencia',
  ajuste_carga_eur: 'Ajuste de carga (€)',
  bonificacion_eur: 'Bonificación (€)',
  total_eur: 'Total (€)',
  porcentaje_iva: 'IVA (%)',
  scenarios: 'Escenarios',
  equipment: 'Equipamiento',
  sensor_equipment: 'Equipamiento',
  environment: 'Estaciones ambientales',
  ammonia: 'Amoníaco',
  water: 'Agua',
  silo: 'Silo',
};
const hidden = new Set([
  'id',
  'created_at',
  'updated_at',
  'deleted_at',
  'revision',
  'explotacion_id',
  'local_pending',
  'operation_uuid',
  'expected_revision',
  'expected_explotacion_id',
  'token_preview',
  'version_esperada',
  'confirm_deactivation',
]);
function title(operation: PendingOperation) {
  const parts = operation.path.split('?')[0].split('/');
  const resource = [...parts].reverse().find((p) => names[p]);
  const action =
    operation.method === 'DELETE'
      ? 'Eliminar'
      : operation.path.endsWith('/cerrar')
        ? 'Cerrar'
        : operation.path.endsWith('/reabrir')
          ? 'Reabrir'
          : operation.method === 'POST'
            ? 'Registrar'
            : 'Actualizar';
  return `${action} ${names[resource ?? ''] ?? 'registro'}`;
}
function serverSensorEquipment(operation: PendingOperation): SensorEquipment | null {
  if (operation.method !== 'PUT' || !operation.actual) return null;
  const parts = new URL(
    operation.payload?.path ?? operation.path,
    'https://local.invalid',
  ).pathname.split('/');
  if (parts[2] !== 'naves' || parts[4] !== 'sensores') return null;
  const id = Number(parts[3]);
  const nave = operation.actual.tables.naves.find((row) => row.id === id) as
    { sensor_equipment?: SensorEquipment | null } | undefined;
  const data = operation.actual.iot.sensores[String(id)] as NaveSensores | undefined;
  return !nave || (nave.sensor_equipment == null && !data) ? null : getSensorEquipment(nave, data);
}
function needsSensorConfirmation(operation: PendingOperation): boolean {
  const body = operation.payload?.body ?? operation.body;
  if (!body || typeof body !== 'object') return false;
  const record = body as Record<string, unknown>;
  if (record.confirm_deactivation === true) return false;
  const equipment = record.equipment as SensorEquipment | undefined;
  const server = serverSensorEquipment(operation);
  return (
    !!server &&
    !!equipment &&
    Array.isArray(equipment.environment) &&
    (server.environment.some((station) => !equipment.environment.includes(station)) ||
      (['ammonia', 'water', 'silo'] as const).some(
        (key) => server[key] && equipment[key] === false,
      ))
  );
}
function serverCopy(operation: PendingOperation): unknown {
  const bundle = operation.actual;
  if (!bundle) return null;
  const url = new URL(operation.payload?.path ?? operation.path, 'https://local.invalid');
  const parts = url.pathname.split('/');
  if (parts[2] === 'naves' && parts[4] === 'sensores') {
    return { equipment: serverSensorEquipment(operation) ?? 'Sin actualizar' };
  }
  const tableMap: Record<string, keyof OfflineBundle['tables']> = {
    camadas: 'camadas',
    naves: 'naves',
    'lotes-nave': 'lotes',
    'entradas-animales': 'entradas',
    'entregas-pienso': 'entregas',
    salidas: 'salidas',
    pesajes: 'pesajes',
    'registros-diarios': 'registros',
  };
  if (parts[2] === 'catalogos') {
    if (parts[3] === 'liquidacion') return bundle.catalogos.liquidacion;
    const curve =
      parts[3] === 'curva-esperada' ? bundle.catalogos.curva_peso : bundle.catalogos.curva_pienso;
    return curve[url.searchParams.get('sexo') ?? ''];
  }
  const body = (operation.payload ? operation.payload.body : operation.body) as Record<
    string,
    unknown
  > | null;
  if (parts[2] === 'camadas' && parts[4] === 'registros-diarios') {
    const ids = bundle.tables.lotes
      .filter((l) => l.camada_id === Number(parts[3]))
      .map((l) => l.id);
    return {
      fecha: body?.fecha,
      partes: bundle.tables.registros.filter(
        (r) => ids.includes(r.lote_nave_id as number) && r.fecha === body?.fecha,
      ),
      observaciones:
        bundle.tables.observaciones.find(
          (o) => o.camada_id === Number(parts[3]) && o.fecha === body?.fecha,
        )?.observaciones ?? null,
    };
  }
  if (parts[2] === 'camadas' && parts[4] === 'liquidacion')
    return (
      bundle.tables.liquidaciones.find((l) => l.camada_id === Number(parts[3]))?.snapshot ?? null
    );
  for (let i = parts.length - 2; i >= 2; i--) {
    const table = tableMap[parts[i]];
    const id = Number(parts[i + 1]);
    if (table && Number.isSafeInteger(id) && id !== 0)
      return bundle.tables[table].find((row) => row.id === id) ?? null;
  }
  return null;
}
function copyRows(
  value: unknown,
  prefix = '',
  bundle?: OfflineBundle,
): Array<{ name: string; value: string }> {
  if (value == null) return [{ name: prefix || 'Datos', value: 'Sin registro' }];
  if (typeof value !== 'object')
    return [
      {
        name: prefix || 'Datos',
        value: typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value),
      },
    ];
  if (Array.isArray(value))
    return value.flatMap((item, index) => {
      const row = item as OfflineRow | null;
      const lote = bundle?.tables.lotes.find((l) => l.id === row?.lote_nave_id);
      const nave = bundle?.tables.naves.find((n) => n.id === (row?.nave_id ?? lote?.nave_id));
      return copyRows(item, `${prefix} · ${nave?.codigo ?? index + 1}`, bundle);
    });
  return Object.entries(value).flatMap(([key, item]) => {
    if (hidden.has(key) || key.endsWith('_id')) return [];
    const label = fields[key] ?? key.replaceAll('_', ' ');
    const name = prefix ? `${prefix} · ${label}` : label;
    if (key === 'environment' && Array.isArray(item))
      return [{ name, value: item.length ? item.join(', ') : 'No instaladas' }];
    if (['ammonia', 'water', 'silo'].includes(key) && typeof item === 'boolean')
      return [{ name, value: item ? 'Instalado' : 'No instalado' }];
    return copyRows(item, prefix ? `${prefix} · ${label}` : label, bundle);
  });
}
function Copy({ value, bundle }: { value: unknown; bundle?: OfflineBundle }) {
  const rows = copyRows(value, '', bundle);
  return (
    <dl className="space-y-1">
      {rows.map((row, index) => (
        <div key={index} className="break-words">
          <dt className="text-xs text-text-muted">{row.name}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Shared durable save state for every business module. */
export function PartsSyncStatus() {
  const { operations, available, error, busy, preparedAt, preparing, connection } =
    usePendingOperations();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('offline:open-sync', show);
    return () => window.removeEventListener('offline:open-sync', show);
  }, []);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [discardId, setDiscardId] = useState<string | null>(null);
  const review = operations.some((o) => ['conflict', 'error', 'auth'].includes(o.status));
  async function act(action: () => Promise<void>) {
    setWorking(true);
    setActionError(null);
    try {
      await action();
      setDiscardId(null);
    } catch (e) {
      setActionError(extractApiMessage(e));
    } finally {
      setWorking(false);
    }
  }
  const count = `${operations.length} ${operations.length === 1 ? 'cambio' : 'cambios'}`;
  const label = review
    ? `Revisar ${count}`
    : `${count} ${operations.length === 1 ? 'pendiente' : 'pendientes'}${busy ? ' · Enviando' : ''}`;
  const notice =
    error ??
    (!available
      ? 'Almacenamiento local no disponible.'
      : preparing && !preparedAt
        ? 'Preparando datos para trabajar sin conexión…'
        : !preparedAt
          ? 'Conecta para preparar la copia sin conexión.'
          : connection === 'offline'
            ? 'Sin conexión · Copia local'
            : connection === 'unavailable'
              ? 'Servidor no disponible · Copia local'
              : null);
  const connectionLabel =
    notice ??
    (connection === 'checking'
      ? 'Comprobando conexión'
      : operations.length
        ? label
        : busy
          ? 'Enviando cambios'
          : 'Sin cambios pendientes');
  const statusLabel =
    operations.length && (notice || connection === 'checking')
      ? `${connectionLabel} · ${label}`
      : connectionLabel;
  const SyncIcon =
    connection === 'offline' || connection === 'unavailable' || !available || error
      ? CloudOff
      : connection === 'checking'
        ? Cloud
        : operations.length || busy
          ? CloudUpload
          : Cloud;
  return (
    <aside className="ui-sync-control">
      <button
        type="button"
        className="ui-icon-button"
        aria-label={`Sincronización: ${statusLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <SyncIcon size={22} strokeWidth={1.8} aria-hidden />
        {connection === 'online' &&
          available &&
          !error &&
          preparedAt &&
          !preparing &&
          !operations.length &&
          !busy && <Check size={12} className="ui-sync-check" aria-hidden />}
        {operations.length > 0 && (
          <span className="ui-header-count" aria-hidden>
            {operations.length > 99 ? '99+' : operations.length}
          </span>
        )}
      </button>
      {!open && (
        <span role="status" aria-atomic="true" className="sr-only">
          {statusLabel}
        </span>
      )}
      {open && (
        <ModalOverlay
          onBackdrop={() => setOpen(false)}
          ariaLabelledby="sync-title"
          placement="bottom"
          className="max-h-[85dvh] max-w-md overflow-y-auto"
        >
          <header className="flex items-center justify-between gap-3">
            <h2 id="sync-title" className="text-lg font-semibold">
              Sincronización
            </h2>
            <button
              type="button"
              aria-label="Cerrar pendientes"
              onClick={() => setOpen(false)}
              className="flex size-11 shrink-0 items-center justify-center rounded-lg"
            >
              <X size={22} aria-hidden="true" />
            </button>
          </header>
          <p role="status" className="my-3 text-sm text-text-secondary">
            {statusLabel}
            {preparedAt && (
              <span className="mt-1 block">
                Copia local preparada: {new Date(preparedAt).toLocaleString('es-ES')}
              </span>
            )}
          </p>
          {operations.length > 0 && (
            <p className="mb-3 text-sm text-text-muted">
              Guardados en este dispositivo. Se enviarán con tu cuenta cuando el servidor esté
              disponible.
            </p>
          )}
          {operations.map((operation) => (
            <section
              key={operation.id}
              className="mb-3 space-y-3 rounded-xl border border-border p-3"
            >
              <h3 className="font-semibold">{title(operation)}</h3>
              <p className="text-xs text-text-muted">
                {new Date(operation.createdAt ?? operation.timestamp).toLocaleString('es-ES')}
              </p>
              <p className="text-sm">
                {operation.status === 'conflict'
                  ? 'El servidor tiene cambios posteriores. Compara las copias antes de elegir.'
                  : operation.status === 'error'
                    ? 'El servidor ha rechazado el cambio. Tu copia local se conserva.'
                    : operation.status === 'auth'
                      ? 'Revisa tu sesión o permisos.'
                      : operation.status === 'sending'
                        ? 'Enviando…'
                        : operation.dependencies.length
                          ? 'Espera a sus registros anteriores.'
                          : 'Pendiente de confirmación.'}
              </p>
              {operation.message && (
                <p className="break-words text-sm text-warning-text">{operation.message}</p>
              )}
              <details className="text-sm">
                <summary className="flex min-h-11 cursor-pointer items-center font-medium">
                  {operation.status === 'conflict' ? 'Comparar copias' : 'Ver copia local'}
                </summary>
                <div className="space-y-3">
                  <div>
                    <h4 className="mb-1 font-medium">Este dispositivo</h4>
                    <Copy
                      value={operation.payload ? operation.payload.body : operation.body}
                      bundle={operation.actual}
                    />
                  </div>
                  {operation.status === 'conflict' && (
                    <div className="border-t border-border pt-2">
                      <h4 className="mb-1 font-medium">Servidor</h4>
                      <Copy value={serverCopy(operation)} bundle={operation.actual} />
                    </div>
                  )}
                </div>
              </details>
              {operation.status === 'conflict' ? (
                <div className="grid gap-2">
                  <p className="text-xs text-text-muted">
                    Conservar el servidor retira este cambio y los que dependen de él.
                  </p>
                  <button
                    type="button"
                    disabled={working || busy}
                    className={button}
                    onClick={() => void act(() => resolveOperationConflict(operation.id, 'server'))}
                  >
                    Conservar servidor
                  </button>
                  {needsSensorConfirmation(operation) && (
                    <p className="text-sm text-warning-text">
                      Tu copia retirará sensores instalados en el servidor; se conserva su
                      histórico.
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={working || busy}
                    className={`${button} bg-accent text-accent-contrast`}
                    onClick={() =>
                      void act(async () => {
                        if (needsSensorConfirmation(operation))
                          await resolveOperationConflict(operation.id, 'local', {
                            confirmSensorDeactivation: true,
                          });
                        else await resolveOperationConflict(operation.id, 'local');
                        await syncPendingOperations();
                      })
                    }
                  >
                    {needsSensorConfirmation(operation)
                      ? 'Confirmar retirada y enviar mi copia'
                      : 'Enviar mi copia local'}
                  </button>
                </div>
              ) : operation.status === 'error' || operation.status === 'auth' ? (
                <div className="space-y-2">
                  <a
                    download={`camadero-pendiente-${operation.id}.json`}
                    href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(operation.body, null, 2))}`}
                    className={`${button} flex items-center justify-center`}
                  >
                    Descargar copia local
                  </a>
                  {discardId === operation.id ? (
                    <div>
                      <p className="mb-2 text-sm">
                        Se descartará este cambio y los que dependan de él. Los datos del servidor
                        se conservan.
                      </p>
                      <button
                        type="button"
                        disabled={working || busy}
                        className={`${button} text-danger`}
                        onClick={() => void act(() => discardPendingOperation(operation.id))}
                      >
                        Confirmar descarte
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={working || busy}
                      className={`${button} w-full text-danger`}
                      onClick={() => setDiscardId(operation.id)}
                    >
                      Descartar copia local
                    </button>
                  )}
                </div>
              ) : null}
            </section>
          ))}
          {actionError && (
            <p role="alert" className="mb-3 text-sm text-danger">
              {actionError}
            </p>
          )}
          <button
            type="button"
            disabled={working || busy || connection === 'offline'}
            className={`${button} flex w-full items-center justify-center gap-2`}
            onClick={() => void act(syncPendingOperations)}
          >
            <RefreshCw size={18} aria-hidden="true" />
            Reintentar sincronización
          </button>
        </ModalOverlay>
      )}
    </aside>
  );
}
